import React from 'react';
import { Close } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import { useGameDialogLogic } from '../hooks/useGameDialogLogic';
import type { CreateGameRequest, Game, GameDialogDefaultValues, GameDialogMode, UpdateGameRequest } from '../../../../types/features/games';
import type { SimpleState } from '../../../../types/locations';
import GameDialogFormFields from './GameDialogFormFields';
import GameDialogImageField from './GameDialogImageField';
import GameDialogLocationFields from './GameDialogLocationFields';
import GameDialogScheduleFields from './GameDialogScheduleFields';
import { adminColors, adminTypography } from '../../../../styles/adminDesignTokens';
import {
  adminButtonSx,
  adminButtonToneSx,
  adminCombineSx,
  adminDialogActionsSx,
  adminDialogContentSx,
  adminDialogSx,
  adminDialogTitleSx,
  adminIconActionButtonSx,
} from '../../../../styles/adminComponentStyles';

interface GameDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateGameRequest | UpdateGameRequest) => Promise<boolean>;
  game?: Game | null;
  loading?: boolean;
  mode: GameDialogMode;
  defaultValues?: GameDialogDefaultValues;
  states: SimpleState[];
}

const GameDialog: React.FC<GameDialogProps> = ({
  open,
  onClose,
  onSubmit,
  game,
  loading = false,
  mode,
  defaultValues,
  states,
}) => {
  const {
    countryDisplayLabel,
    errors,
    existingImageUrl,
    formData,
    imagePreview,
    selectedImage,
    stateDisplayLabel,
    handleClose,
    handleImageSelect,
    handleInputChange,
    handleRemoveImage,
    handleSubmit,
  } = useGameDialogLogic({
    game,
    mode,
    loading,
    defaultValues,
    onClose,
    onSubmit,
    states,
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: adminCombineSx(adminDialogSx, { maxWidth: '570px' }) }}
    >
      <DialogTitle
        sx={adminCombineSx(adminDialogTitleSx, {
          backgroundColor: 'rgb(239, 246, 255)',
          color: adminColors.primary,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 600,
          marginBottom: 0,
        })}
      >
        <Typography component="div" sx={{ ...adminTypography.cardTitle, color: adminColors.text }}>
          {mode === 'create' ? 'Create New Game' : 'Update Game'}
        </Typography> 
      </DialogTitle>

      <DialogContent sx={adminCombineSx(adminDialogContentSx, { p: '20px', pt: '20px !important' })}>
        <Stack spacing={1.25}>
          <GameDialogLocationFields
            countryDisplayLabel={countryDisplayLabel}
            errors={errors}
            stateDisplayLabel={stateDisplayLabel}
          />

          <GameDialogFormFields
            errors={errors}
            formData={formData}
            loading={loading}
            onInputChange={handleInputChange}
          />

          <GameDialogImageField
            imageError={errors.image}
            imageUrlError={errors.image_url}
            existingImageUrl={existingImageUrl}
            hasSelectedImage={!!selectedImage}
            imagePreview={imagePreview}
            imageUrl={formData.image_url}
            loading={loading}
            onImageSelect={handleImageSelect}
            onImageUrlChange={(value) => handleInputChange('image_url', value)}
            onRemoveImage={handleRemoveImage}
          />

          <GameDialogScheduleFields
            errors={errors}
            formData={formData}
            loading={loading}
            onInputChange={handleInputChange}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={adminDialogActionsSx}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          size="small"
          sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary, { height: 40 })}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          size="small"
          sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary, { height: 40 })}
        >
          {loading ? 'Saving...' : mode === 'create' ? 'Create Game' : 'Update Game'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GameDialog;
